
<script setup>
    import { computed, reactive, onMounted, ref } from 'vue'
    import { onAuthStateChanged, signOut } from "firebase/auth";
    import { ref as databaseRef, onValue, query, orderByChild, child, set, get } from "firebase/database";
    import { useRoute } from 'vue-router'
    import {formatToTimeZone} from 'date-fns-timezone';
    import HeaderComponent from "../../components/common/HeaderComponent.vue"
    import AdminBar from '../../components/administrator/AdminBar.vue'
    import SortContractComponent from '../../components/administrator/SortContractComponent.vue'
    import PaginateComponent from 'vuejs-paginate-next'
    import Firebase from "../../firebase_settings/index.js"
    import utilFunctions from "../../firebase_settings/utils.js"

    const DATEFORMAT = 'YYYY/MM';
    const TIME_ZONE_TOKYO = 'Asia/Tokyo';
    const auth = Firebase.auth
    const database = Firebase.database;
    const route = useRoute()

    //企業情報等のリアクティブオブジェクト
    const reactiveObj = reactive({
        //企業ID一覧
        corporateIdList: [],
        //企業一覧
        corporateObjects: [],
        //表示用のオブジェクト
        displayCorporateObjects: [],
        //個別表示用の企業情報
        corporateObject: {},
        //プラン情報
        planInfo: {},
        //個別表示用フラグ
        detailFlag: false,
        //再描画キー
        renderKey: 0,
        //現在のページ
        currentPage: 1,
        //ローディングフラグ
        loadingFlag: false,
        //登録件数に対するアラート表示フラグ
        displayAlert: false,
        //ソート判別フラグ
        sortModalFlag: false,
        //並び替え時のアラート対象を絞るフラグ
        alertFlag: false
    })
    //1ページに表示する件数
    const perPage = 10
    //支払方法
    const paykindDict = {
        0: '請求書',
        1 : 'カード',
        2 : '口座振替',
    }
    //マルチソート
    const multiSort = (
        //アラート対象に絞るか
        alert,
        //ソートオーダー
        sortOrder=[{
            key: 'ContractStartDate',
            order: 'desc'
        }]
    ) => {
        //アラート表示と通常時のデータの切り替え
        const sortObjects = reactiveObj.corporateObjects.filter(element => {
            return !alert || (alert && (element.limitUserFlag || element.limitTerminalFlag))
        })
        return [...sortObjects].sort((a, b) => {
            for (const { key, order } of sortOrder) {
                const valA = a[key];
                const valB = b[key];

                if (typeof valA === "string" && typeof valB === "string") {
                    const result = valA.localeCompare(valB) * (order === "desc" ? -1 : 1);
                    if (result !== 0) return result;
                } else {
                    const result = (valA - valB) * (order === "asc" ? 1 : -1);
                    if (result !== 0) return result;
                }
            }
            return 0;
        });
    };
    //firebaseから企業データを取得
    const getCorporate = async(CorporateIdList) => {
        return new Promise(async (resolve, reject) => {
            reactiveObj.corporateObjects = []
            //プラン情報の取得
            reactiveObj.planInfo = await utilFunctions.getPlanInfo()
            //全企業を一回で取得するとデータ量が大きいので個別にpromise.allで対応する
            await Promise.all(CorporateIdList.map(async (CorporateId) => {
                const addKey = { CorporateId };
                const getCorporateData = databaseRef(database, `CorporateId/${CorporateId}/CorporateInfo`);
                // 取得したデータを処理
                const getData = await new Promise((resolve) => {
                    onValue(getCorporateData, (snapshot) => {
                        if (snapshot.exists() && snapshot.val().ServiceFlag) {
                            const corporateObjects = snapshot.val()
                            const planValue = corporateObjects.PlanValue;
                            //残りの登録可能な利用者件数
                            addKey['remainingUserCount'] = reactiveObj.planInfo[planValue].LimitUserCount - corporateObjects.UserCount
                            //残りの登録可能な端末数
                            addKey['remainingTerminalCount'] = reactiveObj.planInfo[planValue].LimitTerminalCount - corporateObjects.TerminalCount
                            //利用者のアラート判定
                            addKey['limitUserFlag'] = addKey['remainingUserCount'] < reactiveObj.planInfo[planValue].AlertUserCount
                            //端末のアラート判定
                            addKey['limitTerminalFlag'] = addKey['remainingTerminalCount'] < reactiveObj.planInfo[planValue].AlertTerminalCount
                            corporateObjects['ContractStartDate'] = formatToTimeZone(corporateObjects['ContractStartDate'], DATEFORMAT, { timeZone: TIME_ZONE_TOKYO })

                            reactiveObj.corporateObjects.push(Object.assign(corporateObjects, addKey));
                        }
                        resolve()
                    })
                    //表示用の方に格納する
                    reactiveObj.displayCorporateObjects = reactiveObj.corporateObjects;
                })
            }))
            //初回表示は契約開始月の降順でソート
            reactiveObj.displayCorporateObjects = multiSort(false)
            resolve()
        })
    }
    //契約内容の詳細表示
    const CorporateDetail = (flag, corporateObject) => {
        reactiveObj.detailFlag = flag
        reactiveObj.corporateObject = flag ? corporateObject : {}
    }
    //ソートモーダルの表示
    const showSortModal = () => {
        reactiveObj.sortModalFlag = true
    }
    //ソートモーダルを閉じる
    const closeSortModal = async(sortFlag, alertFlag, sortOrder) => {
        reactiveObj.sortModalFlag = false
        //ソートを行う
        if(sortFlag) {
            reactiveObj.displayCorporateObjects = sortOrder.length != 0 ? multiSort(alertFlag, sortOrder) : multiSort(alertFlag)
            //ソートを行った後は必ず1ページ目に戻る
            clickCallback(1)
            reactiveObj.renderKey++;
        }
    }
    //ページネーションを押下した時
    const clickCallback = (pageNum) => {
        reactiveObj.currentPage = Number(pageNum);
    }
    //Nページ目に表示するドア情報を取得
    const getItems = computed(() => {
        let current = reactiveObj.currentPage * perPage;
        let start = current - perPage;
        return reactiveObj.displayCorporateObjects.slice(start, current);
    })
    //現在のページ数を取得
    const getPageCount = computed(() => {
        return Math.ceil(reactiveObj.displayCorporateObjects.length / perPage) ? Math.ceil(reactiveObj.displayCorporateObjects.length / perPage) : 1
    })
    //コンポーネントがマウントされた後に実行
    onMounted(async () => {
        //企業コード一覧を取得する
        reactiveObj.corporateIdList = await utilFunctions.getCorporateIdList()
        //企業一覧を取得する
        await getCorporate(reactiveObj.corporateIdList)
    })
</script>
<template>
    <!-- コメントアウト用 -->
    <HeaderComponent></HeaderComponent>
    <AdminBar></AdminBar>
    <main>
        <div id="loadingDisplay" class="loading" v-show="reactiveObj.loadingFlag">
            <div class="load-text load-blink">処理中...</div>
        </div>
        <div class="managementList" v-if="!reactiveObj.detailFlag">
            <div class="titleArea">
                <p class="title">契約管理</p>
                <button id="sortButton" @click="showSortModal">並び替え</button>
                <SortContractComponent
                    v-show="reactiveObj.sortModalFlag"
                    @executeMethod="closeSortModal">
                </SortContractComponent>
            </div>
            <div class="tableArea">
                <table class="commonTable">
                    <tbody>
                        <tr>
                            <th id="ContractStartDate">契約開始月</th>
                            <th id="CorporateName">企業名</th>
                            <th id="CorporateId">企業コード</th>
                            <th id="PlanName">プラン</th>
                            <th id="remainingUserCount">ユーザー数残</th>
                            <th id="remainingTerminalCount">デバイス残数</th>
                        </tr>
                            <tr v-for="(data, key) in getItems" :key="key" @click="CorporateDetail(true, data)">
                                <td>{{ data.ContractStartDate }}</td>
                                <td>{{ data.CorporateName }}</td>
                                <td>{{ data.CorporateId }}</td>
                                <td>{{ reactiveObj.planInfo[data.PlanValue].PlanName }}</td>
                                <td :style="{ color: `${data.limitUserFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${data.limitUserFlag ? 'bold' : '100'}`}">
                                    {{ data.remainingUserCount >= 0 ? data.remainingUserCount : Math.abs(data.remainingUserCount) + '超過'}}
                                </td>
                                <td :style="{ color: `${data.limitTerminalFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${data.limitTerminalFlag ? 'bold' : '100'}`}">
                                    {{ data.remainingTerminalCount >= 0 ? data.remainingTerminalCount : Math.abs(data.remainingTerminalCount) + '超過'}}
                                </td>
                            </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="managementList" v-else>
            <div class="titleArea">
                <p class="title">契約詳細</p>
                <p id="back" @click="CorporateDetail(false)">＜　一覧に戻る</p>
            </div>
            <div class="tableArea">
                <div class="alertMessage">
                    <p v-show="reactiveObj.corporateObject.limitUserFlag" :style="{ color: `${reactiveObj.corporateObject.limitUserFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${reactiveObj.corporateObject.limitUserFlag ? 'bold' : '100'}`}">
                        {{ reactiveObj.corporateObject.remainingUserCount >= 0 ? `登録ユーザー数が上限に近づいています。(残り${reactiveObj.planInfo[reactiveObj.corporateObject.PlanValue].LimitUserCount - reactiveObj.corporateObject.UserCount}名)` : `登録ユーザー数が上限を超過しています。(${Math.abs(reactiveObj.corporateObject.remainingUserCount)}名の超過)` }}
                    </p>
                    <p v-show="reactiveObj.corporateObject.limitTerminalFlag" :style="{ color: `${reactiveObj.corporateObject.limitTerminalFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${reactiveObj.corporateObject.limitTerminalFlag ? 'bold' : '100'}`}">
                        {{ reactiveObj.corporateObject.remainingTerminalCount >= 0 ? `登録デバイス数が上限に近づいています。(残り${reactiveObj.planInfo[reactiveObj.corporateObject.PlanValue].LimitTerminalCount - reactiveObj.corporateObject.TerminalCount}台)` : `登録デバイス数が上限を超過しています。(${Math.abs(reactiveObj.corporateObject.remainingTerminalCount)}台の超過)` }}
                    </p>
                </div>
                <table class="detailTable">
                    <tbody>
                        <tr>
                            <td class="leftText">企業名</td>
                            <td class="rightText">{{ reactiveObj.corporateObject.CorporateName }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">企業コード</td>
                            <td class="rightText">{{ reactiveObj.corporateObject.CorporateId }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">担当者名</td>
                            <td class="rightText">{{ reactiveObj.corporateObject.ContractPerson }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">メールアドレス</td>
                            <td class="rightText">{{ reactiveObj.corporateObject.Email }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">プラン名</td>
                            <td class="rightText">{{ reactiveObj.planInfo[reactiveObj.corporateObject.PlanValue].PlanName }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">契約開始月</td>
                            <td class="rightText">{{ reactiveObj.corporateObject.ContractStartDate }}</td>
                        </tr>
                        <tr>
                            <td class="leftText">支払い方法</td>
                            <td class="rightText">
                                {{ `${reactiveObj.corporateObject.PayKind  ? paykindDict[reactiveObj.corporateObject.PayKind] : '請求書'} / 
                                    ${reactiveObj.corporateObject.Payment == 1 ? '月' : '年'}払い` }}<br>
                                {{ `(次回お支払い：${formatToTimeZone(reactiveObj.corporateObject.EndExpectedDate, DATEFORMAT, { timeZone: TIME_ZONE_TOKYO })}）` }}
                            </td>
                        </tr>   
                        <tr>
                            <td class="leftText">登録ユーザー数</td>
                            <td class="rightText" :style="{ color: `${reactiveObj.corporateObject.limitUserFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${reactiveObj.corporateObject.limitUserFlag ? 'bold' : '100'}`, 'border-color': '#000000'}">
                                {{ reactiveObj.corporateObject.UserCount }} / {{ reactiveObj.planInfo[reactiveObj.corporateObject.PlanValue].LimitUserCount }} 名
                            </td>
                        </tr>
                        <tr class="notButtom">
                            <td class="leftText">登録デバイス数</td>
                            <td class="rightText" :style="{ color: `${reactiveObj.corporateObject.limitTerminalFlag ? '#ff0000' : '#000000'}`, 'font-weight': `${reactiveObj.corporateObject.limitTerminalFlag ? 'bold' : '100'}`, 'border-color': '#000000'}">
                                {{ reactiveObj.corporateObject.TerminalCount }} / {{ reactiveObj.planInfo[reactiveObj.corporateObject.PlanValue].LimitTerminalCount }} 台
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="paginationArea" :key="`${reactiveObj.renderKey}`" >
            <PaginateComponent
                :page-count="getPageCount"
                :click-handler="clickCallback"
                :page-range="5"
                :prev-text="'<'"
                :next-text="'>'"
                :container-class="'pagination'"
                :page-class="'page-item'">
            </PaginateComponent>
        </div>
    </main>
</template>

<style scoped>
    .managementList {
        padding: 0 5% 0 25%;
        background-color: #e7e6e6;
    }
    #back {
        position: absolute;
        top: 5%;
        cursor: pointer;
    }
    #sortButton {
        color: #FFFFFF;
        position: relative;
        display: flex;
        margin-left: auto;
        top: -40px;
        border: solid 3px #00AEEB;
        background: #00AEEB;
    }
    .commonTable td {
        border-color: black;
        cursor: pointer;
    }
    .commonTable {
        border-color: black;
        th {
            position: relative;
        }
    }
    .alertMessage {
        padding: 0 0 20px 0;
    }
    .detailTable {
        width: 100%;
        margin: 0 0 0 0;
        border-collapse: collapse;
        border: none;
        tr {
            height: 60px;
        }
        td {
            background: #dbdbdb;
            border-left: solid 1px;
            border-top: solid 1px;
        }
        td:first-child {
            border-left:none;
        }
        tr:first-child td {
            border-top:none;
        }
        tr:first-child>*:first-child{
            border-radius: 20px 0 0 0;
        }
        tr:first-child>*:last-child{
            border-radius: 0 20px 0 0;
        }
        tr:last-child>*:last-child{
            border-radius: 0 0 20px 0;
        }
        tr:last-child>*:first-child{
            border-radius: 0 0 0 20px;
        }
        tr:nth-child(odd) td {
            background: #c4c4c4;
        }
        .leftText {
            width: 30%;
            text-align: left;
            left: 20px;
            padding: 0 0 0 20px;
        }
        .rightText {
            width: 70%;
            text-align: left;
            left: 20px;
            padding: 0 0 0 20px;
        }
    }
    @media (max-width: 1150px) {
        .managementList{
            padding: 0 5% 0 5%;
        }
        .tableArea {
            overflow-x: scroll;
        }
    }
</style>
